import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import NamaURLS from "@/utils/constants/NamaURLS.ts";
import {useErrorsStore} from "@/stores/ToastDataStore.ts";
import {registerTempoLanguage} from "@/utils/common/TempoLanguageConfig.ts";

export default class MonacoEditorUtils {
  private static sqlTableAndColumnNames: string[] | undefined = undefined;
  private static sqlTableAndColumnNamesFetchPromise?:  Promise<string[] | undefined>;
  private static monacoSqlSuggestionsInitialized = false;
  private static monacoTempoInitialized = false;

  static async addSQLSuggestions() {
    if (ObjectChecker.isNotNullOrUndefined(this.sqlTableAndColumnNamesFetchPromise))
      return;
    if (ObjectChecker.isNullOrUndefined(this.sqlTableAndColumnNames))
      await this.fetchSQLAllTablesAndColumnNames();
    await this.initializeMonacoSQLSuggestions();
  }

  static async addTempoSuggestions() {
    if (this.monacoTempoInitialized)
      return;
    this.monacoTempoInitialized = true;
    const {loader} = await import("@guolao/vue-monaco-editor");
    const monaco = await loader.init();
    registerTempoLanguage(monaco);
  }

  private static async initializeMonacoSQLSuggestions() {
    if (this.monacoSqlSuggestionsInitialized)
      return;
    this.monacoSqlSuggestionsInitialized = true;
    const {loader} = await import("@guolao/vue-monaco-editor");
    loader.init().then((monaco) => {
      monaco.languages.registerCompletionItemProvider('sql', {
        provideCompletionItems: (model, position) => {
          const word = model.getWordUntilPosition(position);
          const range = {
            startLineNumber: position.lineNumber,
            endLineNumber: position.lineNumber,
            startColumn: word.startColumn,
            endColumn: word.endColumn
          };

          const keywords = [
            'SELECT', 'FROM', 'WHERE', 'INSERT', 'UPDATE', 'DELETE', 'CREATE', 'ALTER', 'DROP',
            'TABLE', 'INDEX', 'VIEW', 'JOIN', 'INNER', 'LEFT', 'RIGHT', 'OUTER', 'ON',
            'GROUP BY', 'ORDER BY', 'HAVING', 'DISTINCT', 'TOP', 'LIMIT', 'OFFSET',
            'AND', 'OR', 'NOT', 'IN', 'BETWEEN', 'LIKE', 'IS', 'NULL', 'AS',
            'COUNT', 'SUM', 'AVG', 'MIN', 'MAX', 'CASE', 'WHEN', 'THEN', 'ELSE', 'END',
            'UNION', 'INTERSECT', 'EXCEPT', 'EXISTS', 'ANY', 'ALL',
            'ASC', 'DESC', 'PRIMARY KEY', 'FOREIGN KEY', 'CONSTRAINT', 'DEFAULT',
            'INT', 'VARCHAR', 'NVARCHAR', 'TEXT', 'DATETIME', 'DATE', 'DECIMAL', 'FLOAT', 'BIT'
          ];

          const functions = [
            'COUNT', 'SUM', 'AVG', 'MIN', 'MAX', 'CAST', 'CONVERT', 'GETDATE', 'DATEADD', 'DATEDIFF',
            'LEN', 'SUBSTRING', 'REPLACE', 'UPPER', 'LOWER', 'TRIM', 'LTRIM', 'RTRIM',
            'CONCAT', 'COALESCE', 'ISNULL', 'NULLIF', 'ROW_NUMBER', 'RANK', 'DENSE_RANK',
            'OVER', 'PARTITION BY'
          ];

          const snippets = [
            {label: '{XIN,column,param}', insertText: '{XIN,${1:column},${2:param}}', detail: 'Dynamic IN clause placeholder'},
            {
              label: '{XBETWEEN,column,fromParam,toParam}',
              insertText: '{XBETWEEN,${1:column},${2:fromParam},${3:toParam}}',
              detail: 'Dynamic BETWEEN clause placeholder'
            },
          ];

          const suggestions = [
            ...keywords.map(keyword => ({
              label: keyword,
              kind: monaco.languages.CompletionItemKind.Keyword,
              insertText: keyword,
              range: range,
              detail: 'SQL Keyword'
            })),
            ...functions.map(func => ({
              label: func,
              kind: monaco.languages.CompletionItemKind.Function,
              insertText: func + '($0)',
              insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
              range: range,
              detail: 'SQL Function'
            })),
            ...snippets.map(placeholder => ({
              label: placeholder.label,
              kind: monaco.languages.CompletionItemKind.Snippet,
              insertText: placeholder.insertText,
              insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
              range: range,
              detail: placeholder.detail
            })),
            ...this.sqlTableAndColumnNames?.map(tableNameOrColumn => {
              const isTable = /^\p{Lu}/u.test(tableNameOrColumn);
              return ({
                label: tableNameOrColumn,
                kind: isTable ? monaco.languages.CompletionItemKind.Class : monaco.languages.CompletionItemKind.Field,
                insertText: tableNameOrColumn,
                range: range,
                detail: isTable ? "Table" : "Field"
              });
            }) ?? []
          ];
          return {suggestions};
        }
      });

      monaco.languages.registerHoverProvider('sql', {
        provideHover: (model, position) => {
          const word = model.getWordAtPosition(position);
          if (!word)
            return null;
          const sqlDocs: Record<string, string> = {
            'SELECT': 'Retrieves rows from a database table',
            'FROM': 'Specifies the table to select data from',
            'WHERE': 'Filters records based on specified conditions',
            'JOIN': 'Combines rows from two or more tables',
            'GROUP BY': 'Groups rows that have the same values',
            'ORDER BY': 'Sorts the result set',
            'COUNT': 'Returns the number of rows',
            'SUM': 'Returns the sum of a numeric column',
            'AVG': 'Returns the average value of a numeric column',
            'COALESCE': 'Returns first not null value',
            'OUTER APPLY': 'Left joins to a subquery',
            'CROSS APPLY': 'Inner joins to a subquery',
          };
          const doc = sqlDocs[word.word.toUpperCase()];
          if (doc)
            return {
              range: new monaco.Range(position.lineNumber, word.startColumn, position.lineNumber, word.endColumn),
              contents: [{value: `**${word.word.toUpperCase()}**\n\n${doc}`}]
            };
          return null;
        }
      });
    });
  }

  private static async performSQLTableAndColumnNamesFetch(retryCount: number = 1): Promise<string[] | undefined> {
    try {
      const data: any = await NamaCommon.namaFetch(NamaURLS.FetchTableAndColumnNames, {
        noLoadingIndicator: true,
        method: "GET"
      });

      if (!NamaCommon.isSuccess(data))
        this.sqlTableAndColumnNames = [];
      else
        this.sqlTableAndColumnNames = data.list
      return this.sqlTableAndColumnNames;
    } catch (e) {
      console.log(e);
      if (retryCount < 5)
        return await this.performSQLTableAndColumnNamesFetch(retryCount + 1);
      else
        useErrorsStore().pushErrorStr("Could not fetch sql table and column names, please review console logs");
    } finally {
      this.sqlTableAndColumnNamesFetchPromise = undefined;
    }
  }

  static async fetchSQLAllTablesAndColumnNames(): Promise<string[] | undefined> {
    if (ObjectChecker.isNotNullOrUndefined(this.sqlTableAndColumnNames))
      return this.sqlTableAndColumnNames;
    else if (ObjectChecker.isNotNullOrUndefined(this.sqlTableAndColumnNamesFetchPromise))
      return this.sqlTableAndColumnNamesFetchPromise;
    this.sqlTableAndColumnNamesFetchPromise = this.performSQLTableAndColumnNamesFetch();
    return this.sqlTableAndColumnNamesFetchPromise;
  }
}
